import { getYouTubeEmbedValidationMessage, normalizeYouTubeEmbedInput } from "./youtubeEmbed";

export const MAX_STORE_BANNERS = 5;
export const PROMO_CARD_SLOTS = 3;
export const BANNER_ACCEPT = "image/jpeg,image/png,image/webp";
export const BANNER_MAX_SIZE_MB = 3;

/** Recommended upload specs shown to vendors in the store builder */
export const IMAGE_GUIDES = {
  heroBanner: {
    label: "Hero banner",
    width: 1920,
    height: 640,
    ratio: "3:1",
    ratioDescription: "Wide landscape",
    minWidth: 1200,
    formats: "PNG, JPG, WEBP",
    tip: "Keep important text and logos near the center — sides may crop on smaller screens.",
  },
  promoCard: {
    label: "Category highlight card",
    width: 800,
    height: 600,
    ratio: "4:3",
    ratioDescription: "Landscape",
    minWidth: 600,
    formats: "PNG, JPG, WEBP",
    tip: "Use the same size for all three cards so the row looks even on your store.",
  },
  highlightVideo: {
    label: "Spotlight video frame",
    width: 1697,
    height: 602,
    ratio: "1697:602",
    ratioDescription: "Wide cinematic",
    minWidth: 1200,
    formats: "YouTube embed only",
    tip: "Paste an official YouTube embed or watch link. The video renders full-width below your category highlights.",
  },
};

export const formatDimensionGuide = (guide) =>
  `${guide.width} × ${guide.height} px · ${guide.ratio} (${guide.ratioDescription})`;

export const createEmptyBanner = (order = 0) => ({
  id: `banner_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  image: "",
  title: "",
  subtitle: "",
  link: "",
  order,
});

export const createEmptyPromoCards = () =>
  Array.from({ length: PROMO_CARD_SLOTS }, (_, index) => ({
    id: `promo_${index + 1}`,
    image: "",
    title: "",
    link: "",
  }));

export const createDefaultStoreConfig = () => ({
  tagline: "",
  about: "",
  accentColor: "#FF6C6C",
  isPublished: true,
  showContactBar: true,
  banners: [],
  promoCards: createEmptyPromoCards(),
  highlightVideo: {
    embedUrl: "",
    title: "",
  },
  lastSavedAt: null,
});

export const normalizePromoCards = (cards) => {
  const defaults = createEmptyPromoCards();
  if (!Array.isArray(cards)) return defaults;

  return defaults.map((slot, index) => ({
    ...slot,
    ...(cards[index] || {}),
    id: cards[index]?.id || slot.id,
    title: cards[index]?.title || "",
    link: cards[index]?.link || "",
    image: cards[index]?.image || "",
  }));
};

export const isPromoCardsTouched = (cards) =>
  normalizePromoCards(cards).some((card) => card.image || card.title?.trim());

export const arePromoCardsComplete = (cards) =>
  normalizePromoCards(cards).every((card) => Boolean(card.image));

export const getPromoCardsValidationMessage = (cards) => {
  if (!isPromoCardsTouched(cards)) return null;
  if (arePromoCardsComplete(cards)) return null;

  const uploaded = normalizePromoCards(cards).filter((card) => card.image).length;
  return `Category highlights need all 3 images (${uploaded}/3). Finish uploading or clear the section.`;
};

export const normalizeHighlightVideo = (highlightVideo = {}) => ({
  embedUrl: normalizeYouTubeEmbedInput(highlightVideo.embedUrl || "") || "",
  title: String(highlightVideo.title || "").trim().slice(0, 80),
});

export const isHighlightVideoTouched = (highlightVideo = {}) =>
  Boolean(highlightVideo?.embedUrl?.trim() || highlightVideo?.title?.trim());

export const getHighlightVideoValidationMessage = (highlightVideo = {}) => {
  const normalized = normalizeHighlightVideo(highlightVideo);
  if (!isHighlightVideoTouched(highlightVideo)) return null;

  const urlError = getYouTubeEmbedValidationMessage(highlightVideo.embedUrl);
  if (urlError) return urlError;
  if (!normalized.embedUrl) {
    return "Add a valid YouTube embed link to publish your spotlight video.";
  }

  return null;
};

export const normalizeStoreConfig = (config = {}) => ({
  ...createDefaultStoreConfig(),
  ...config,
  banners: Array.isArray(config.banners)
    ? [...config.banners].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [],
  promoCards: normalizePromoCards(config.promoCards),
  highlightVideo: normalizeHighlightVideo(config.highlightVideo || {}),
});

export const isBase64Image = (value) =>
  typeof value === "string" && value.startsWith("data:image");

export const isRemoteImageUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value);

/** Send base64 for new/changed uploads; keep CDN URLs for unchanged images. */
export const resolveImageForApi = (image) => {
  if (!image) return "";
  if (isBase64Image(image)) return image;
  if (isRemoteImageUrl(image)) return image;
  return image;
};

export const prepareStorePayloadForApi = (config) => {
  const normalized = normalizeStoreConfig(config);

  return {
    ...normalized,
    banners: normalized.banners.map((banner) => ({
      ...banner,
      image: resolveImageForApi(banner.image),
    })),
    promoCards: normalized.promoCards.map((card) => ({
      ...card,
      image: resolveImageForApi(card.image),
    })),
    lastSavedAt: new Date().toISOString(),
  };
};
